import axios, { isAxiosError } from 'axios'
import i18n from '@/i18n/config'
import { useAuthStore } from '@/stores/authStore'
import { clientHeaders } from '@/api/clientInfo'
import { authBreadcrumb, toAuthCode } from '@/api/authDiagnostics'
import { withCrossTabLock } from '@/utils/crossTabLock'
import { API_ENDPOINTS, AUTH_PERSIST_KEY, STORAGE_KEYS } from '@/utils/constants'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4545/api'

const REFRESH_LOCK_NAME = 'easya-auth-refresh'
const REFRESH_TIMEOUT_MS = 20_000
/** Отпечаток токена, который сервер окончательно отказался обменять */
const REJECTED_TOKEN_KEY = 'easya_refresh_rejected'

/**
 * Итог обмена токена:
 * - `ok` — есть рабочий токен (обменяли сами или его уже обменяла другая вкладка);
 * - `failed` — сессия закончилась окончательно (401 обмена, токена нет): сбросить и сообщить причину;
 * - `ended` — сессию уже завершили в другой вкладке: только локальная уборка, без отчёта;
 * - `transient` — временный сбой (сеть, 5xx, 429): сессию не трогать, запрос завершается ошибкой.
 */
export type RefreshOutcome =
  | { status: 'ok'; token: string }
  | { status: 'failed'; reason: string }
  | { status: 'ended' }
  | { status: 'transient'; reason: string }

interface RefreshResponse {
  success?: boolean
  data?: { token?: string; expires_in?: number }
}

let inFlight: { token: string; promise: Promise<RefreshOutcome> } | null = null

function resolveApiLanguage(): string {
  const currentLanguage = i18n.language || 'ru'
  const normalizedLanguage = currentLanguage.split('-')[0].toLowerCase()

  return ['ru', 'en'].includes(normalizedLanguage) ? normalizedLanguage : 'ru'
}

/** Токен из общего для вкладок хранилища — он актуальнее памяти вкладки */
export function readStoredToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
  } catch {
    return null
  }
}

/** В хранилище сессия явно завершена (выход в другой вкладке), а не стёрта целиком */
function isSessionEndedInStorage(): boolean {
  try {
    const raw = localStorage.getItem(AUTH_PERSIST_KEY)
    if (!raw) {
      return false
    }
    const parsed = JSON.parse(raw) as { state?: { isAuthenticated?: unknown } }

    return parsed.state?.isAuthenticated === false
  } catch {
    return false
  }
}

function missingTokenOutcome(): RefreshOutcome {
  return isSessionEndedInStorage() ? { status: 'ended' } : { status: 'failed', reason: 'no_token' }
}

/**
 * Память вкладки отстала от хранилища (другая вкладка обменяла токен, а
 * событие storage ещё не дошло) — перечитать сессию из хранилища.
 */
function syncMemoryWithStorage(storedToken: string): void {
  if (useAuthStore.getState().token === storedToken) {
    return
  }

  authBreadcrumb('stale_memory_token', 'warning')
  void useAuthStore.persist.rehydrate()
}

function reuseRotatedToken(token: string): RefreshOutcome {
  syncMemoryWithStorage(token)
  authBreadcrumb('refresh_reused', 'info')

  return { status: 'ok', token }
}

/** Хвост подписи: достаточно, чтобы узнать токен, и не копия самого токена */
function tokenFingerprint(token: string): string {
  return token.slice(-24)
}

/**
 * Отказ обмена виден другим вкладкам: вкладка, получившая отказ, стирает
 * сессию уже после снятия блокировки, и следующая в очереди иначе успела бы
 * отправить на обмен тот же токен. Лишний refresh_failed из соседней вкладки
 * в журнале выглядел бы как гонка обмена — ровно тот признак, по которому
 * ищут проблему у нас.
 */
function markRejected(token: string): void {
  try {
    localStorage.setItem(REJECTED_TOKEN_KEY, tokenFingerprint(token))
  } catch {
    // хранилище недоступно — соседняя вкладка просто повторит обмен
  }
}

function isMarkedRejected(token: string): boolean {
  try {
    return localStorage.getItem(REJECTED_TOKEN_KEY) === tokenFingerprint(token)
  } catch {
    return false
  }
}

function refreshErrorCode(data: unknown): string {
  const code = (data as { error?: unknown } | undefined)?.error

  return typeof code === 'string' && code !== '' ? toAuthCode(code) : 'unknown'
}

async function refreshUnderLock(expectedToken: string): Promise<RefreshOutcome> {
  const current = readStoredToken()
  if (!current) {
    return missingTokenOutcome()
  }
  // Пока ждали блокировку, токен обменяла другая вкладка
  if (current !== expectedToken) {
    return reuseRotatedToken(current)
  }
  // Этот же токен другая вкладка только что получила отказом и сейчас
  // завершает сессию — её отчёт единственный, здесь только уборка
  if (isMarkedRejected(current)) {
    authBreadcrumb('refresh_rejected_in_other_tab', 'info')

    return { status: 'ended' }
  }

  authBreadcrumb('refresh_start')

  try {
    const response = await axios.post<RefreshResponse>(
      `${API_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
      {},
      {
        timeout: REFRESH_TIMEOUT_MS,
        headers: {
          ...clientHeaders(),
          Authorization: `Bearer ${current}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': resolveApiLanguage(),
        },
      },
    )

    const payload = response.data?.data
    if (!response.data?.success || !payload?.token) {
      authBreadcrumb('refresh_transient', 'warning', { reason: 'invalid_response' })

      return { status: 'transient', reason: 'refresh_invalid_response' }
    }

    // Пока шёл запрос, сессию завершили или сменили в другой вкладке —
    // не перетирать хранилище своим токеном
    const after = readStoredToken()
    if (after !== current) {
      return after ? reuseRotatedToken(after) : missingTokenOutcome()
    }

    useAuthStore.getState().updateToken(payload.token, payload.expires_in)
    authBreadcrumb('refresh_ok')

    return { status: 'ok', token: payload.token }
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined

    if (status === 401) {
      const code = refreshErrorCode(isAxiosError(error) ? error.response?.data : undefined)
      // token_blacklisted: токен могла обменять вкладка, которой блокировка
      // недоступна, — сначала перечитать хранилище
      const after = readStoredToken()
      if (after && after !== current) {
        return reuseRotatedToken(after)
      }
      if (!after) {
        return missingTokenOutcome()
      }

      markRejected(current)
      authBreadcrumb('refresh_failed', 'error', { code })

      return { status: 'failed', reason: `refresh_401:${code}` }
    }

    const reason = status !== undefined ? `refresh_${status}` : 'refresh_network'
    authBreadcrumb('refresh_transient', 'warning', { reason })

    return { status: 'transient', reason }
  }
}

/**
 * Единая точка обмена JWT (interceptor 401 и продление в useAuthBootstrap).
 *
 * `failedToken` — токен, с которым запрос получил 401. Если в хранилище уже
 * другой токен, его обменяла другая вкладка (или эта же чуть раньше), и
 * повторный обмен не нужен: старый токен сервер уже занёс в чёрный список.
 *
 * Обмен идёт под блокировкой между вкладками: вкладки, проснувшиеся разом,
 * обменивают токен по очереди, и каждая следующая берёт уже обменянный.
 */
export async function refreshAccessToken(failedToken?: string | null): Promise<RefreshOutcome> {
  // Сессия админа под пользователем не продлевается (как и раньше)
  if (useAuthStore.getState().isImpersonating) {
    return { status: 'failed', reason: 'impersonation_401' }
  }

  const stored = readStoredToken()
  if (!stored) {
    return missingTokenOutcome()
  }
  if (failedToken && failedToken !== stored) {
    return reuseRotatedToken(stored)
  }
  syncMemoryWithStorage(stored)

  if (inFlight?.token === stored) {
    return inFlight.promise
  }

  const promise = withCrossTabLock(REFRESH_LOCK_NAME, () => refreshUnderLock(stored))
    .catch((): RefreshOutcome => {
      // Сбой самого клиента (хранилище, блокировка) — не повод разлогинивать
      authBreadcrumb('refresh_transient', 'warning', { reason: 'client_error' })

      return { status: 'transient', reason: 'refresh_client_error' }
    })
    .finally(() => {
      if (inFlight?.token === stored) {
        inFlight = null
      }
    })
  inFlight = { token: stored, promise }

  return promise
}
